import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, Button } from '@etherisc/ui-kit';
import { 
  RefreshCwIcon, 
  CheckCircleIcon, 
  AlertCircleIcon, 
  ClockIcon,
  ShieldIcon,
} from 'lucide-react';
import { WizardStepProps } from '../../../framework/wizard';
import type { QuoteWizardData } from '../QuoteWizard';
import type { ProtectionLevels } from '../../../types/quote';
import { createCompleteQuote } from '../utils/quoteHelpers';
import { formatCurrency as formatPricingCurrency } from '../utils/pricingCalculator';
import { quoteService } from '../../../services/quoteService';
import type { QuotationAggregates } from '../../../types/quote';
// Real imports - following coding rules: show errors if missing, don't create fallbacks
import { useQuotation } from '../../../hooks/useQuotation';
import type { QuotationResult, RawScenarioData } from '../../../services/quotationService';
// import { QuotationProgress } from '../QuotationProgress';

// No fallback/mock data - follow coding rules: show errors, don't hide them

// Local interfaces for quotation data handling
interface QuotationMetadata {
  calculationDate?: string;
  quotationScript?: string;
  processingTimeSeconds?: number;
  coveragePeriod?: Record<string, unknown>;
  farmersCount?: number;
  gitCommit?: string;
  jobId?: string;
  status?: string;
  location?: {
    latitude?: number;
    longitude?: number;
  } | null;
  [key: string]: unknown;
}

// Quotation result data structures
interface QuotationScenario {
  peril?: string;
  strikeReturnPeriod?: number;
  exhaustReturnPeriod?: number;
  premium?: number;
  protectionLevel?: number;
  totalPayable?: number;
  strikeThreshold?: number;
  exhaustThreshold?: number;
  paymentPerMm?: number;
  anrp?: number;
  grp?: number;
  taxAmount?: number;
  subsidyAmount?: number;
}

// Raw stored scenario data (snake_case from R-Server)
interface RawStoredScenario {
  deficit_strike_return_period?: number;
  deficit_exhaust_return_period?: number;
  deficit_strike_threshold?: number;
  deficit_exhaust_threshold?: number;
  deficit_premium?: number;
  total_payable?: number;
  deficit_payment_per_mm?: number;
  ANRP?: number;
  GRP?: number;
  tax_amount?: number;
  subsidy_amount?: number;
  [key: string]: unknown;
}

// QuotationResultData interface removed - was unused

interface StoredQuotationResults {
  output?: {
    quotation_result?: Array<{
      data?: RawStoredScenario[];
    }>;
    metadata?: QuotationMetadata;
  };
}

// interface ProcessedQuotationResult {
//   peril: string;
//   strikeReturnPeriod: number;
//   exhaustReturnPeriod: number;
//   strikeThreshold: number;
//   exhaustThreshold: number;
//   premium: number;
//   totalPayable: number;
//   paymentPerMm: number;
//   anrp: number;
//   grp: number;
//   taxAmount: number;
//   subsidyAmount: number;
//   fullScenario: Record<string, unknown>;
// }

interface ProtectionLevelSlider {
  peril: string;
  perilName: string;
  currentStrike: number; // Current strike return period
  currentExhaust: number; // Current exhaust return period
  availableStrikePeriods: number[]; // Available strike return periods
  availableExhaustPeriods: number[]; // Available exhaust return periods
  availableOptions: Array<{
    strike: number; // Strike return period
    exhaust: number; // Exhaust return period
    premium: number;
    protectionLevel: number;
    // Additional calculated values from R-Server
    totalPayable: number;
    strikeThreshold: number;
    exhaustThreshold: number;
    paymentPerMm: number;
    anrp: number;
    grp: number;
    taxAmount: number;
    subsidyAmount: number;
  }>;
}

// interface ProtectionOption {
//   level: number;
//   label: string;
//   strikeValue: number;
//   exhaustValue: number;
//   premium: number;
// }

export function ProtectionLevelStepNew({ 
  data, 
  updateData
}: WizardStepProps<QuoteWizardData>) {
  
  // Note: Using shared createCompleteQuote utility to avoid code duplication
  
  // Initialize state from persisted data if available (for when navigating back to this step)
  const hasExistingResults = !!(data.quote?.quotationResults);
  const initialSliders = hasExistingResults && data.quote && (data.quote.quotationResults as Record<string, unknown>)?.processedSliders
    ? (data.quote.quotationResults as Record<string, unknown>).processedSliders as ProtectionLevelSlider[]
    : [];
  
  const [protectionSliders, setProtectionSliders] = useState<ProtectionLevelSlider[]>(initialSliders);
  // Only mark as complete if we have both results AND processed sliders
  const [isCalculationComplete, setIsCalculationComplete] = useState(hasExistingResults && initialSliders.length > 0);
  
  // Check if there's a stored error from a previous failed calculation in quotationResults
  const quotationResults = data.quote?.quotationResults as Record<string, unknown> | undefined;
  const resultStatus = quotationResults?.status as string | undefined;
  const isFailedCalculation = resultStatus === 'failed' || resultStatus === 'error' || resultStatus === 'cancelled';
  
  // Extract and format error message (same logic as quotationService.ts)
  let storedError: string | undefined;
  if (isFailedCalculation) {
    let errorMessage = (quotationResults?.error || quotationResults?.error_details || 'Calculation failed') as string;
    
    // Try to parse and format if it's JSON
    try {
      const parsed = JSON.parse(errorMessage);
      if (parsed.error) {
        errorMessage = parsed.error;
      } else if (typeof parsed === 'object') {
        errorMessage = Object.entries(parsed)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');
      }
    } catch {
      // Not JSON, keep as is
    }
    
    // Unescape special characters
    if (typeof errorMessage === 'string') {
      errorMessage = errorMessage
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\r/g, '\r')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
    }
    
    storedError = errorMessage;
  }
  
  const [calculationError, setCalculationError] = useState<string | null>(storedError || null);
  const [isDirty, setIsDirty] = useState(false);
  // Initialize quotation metadata from persisted data if available
  const initialMetadata = hasExistingResults && data.quote && (data.quote.quotationResults as Record<string, unknown>)?.metadata
    ? (data.quote.quotationResults as Record<string, unknown>).metadata as QuotationMetadata
    : null;
  const [quotationMetadata, setQuotationMetadata] = useState<QuotationMetadata | null>(initialMetadata);
  const [activeTab, setActiveTab] = useState<'pricing' | 'details'>('pricing');
  // Note: Removed parametersChanged state - not needed without complex parameters
  
  // Removed: processingFreshResults - no longer needed with single source of truth
  const freshResultsProcessedRef = useRef(false); // Use ref for immediate synchronous updates
  
  // Monitor recalcNeeded flag from wizard data (now managed by framework)
  const [showRecalcPrompt, setShowRecalcPrompt] = useState(false);
  const [aggregates, setAggregates] = useState<QuotationAggregates | null>(null);
  const [loadingAggregates, setLoadingAggregates] = useState(false);
  
  useEffect(() => {
    
    if (data.recalcNeeded && isCalculationComplete) {
      setShowRecalcPrompt(true);
    } else {
      setShowRecalcPrompt(false);
    }
  }, [data.recalcNeeded, isCalculationComplete, showRecalcPrompt]);

  // Load quotation aggregates from backend
  // Load when quotation results exist OR when protection levels exist (indicates calculation was done)
  useEffect(() => {
    const loadAggregates = async () => {
      if (!data.quote?.id) {
        return;
      }

      // Load if we have quotation results OR protection levels (both indicate calculation was done)
      const hasQuotationResults = !!data.quote?.quotationResults;
      const hasProtectionLevels = !!data.quote?.protectionLevels && Object.keys(data.quote.protectionLevels).length > 0;
      
      if (!hasQuotationResults && !hasProtectionLevels) {
        return;
      }

      setLoadingAggregates(true);
      try {
        // Pass protection levels to backend so it can calculate aggregates correctly
        const protectionLevels = data.quote?.protectionLevels;
        const response = await quoteService.getQuotationSummary(data.quote.id, protectionLevels);
        
        // The API client returns the data directly (unwrapped)
        const aggregatesData = response?.quotationResults?.aggregates;
        
        // Always set aggregates if they exist (even if zero, to ensure we have the structure)
        // The pricing summary will handle zero values appropriately and show a warning
        if (aggregatesData && aggregatesData.total) {
          setAggregates(aggregatesData);
        } else if (hasProtectionLevels || hasQuotationResults) {
          // If we have calculation indicators but no aggregates, this might indicate an error
          // Set aggregates to null so the pricing summary can show appropriate message
          console.warn('Calculation results exist but aggregates are not available', {
            hasQuotationResults,
            hasProtectionLevels,
            quoteId: data.quote.id
          });
        }
      } catch (error) {
        console.error('Error loading quotation aggregates on Page 5:', error);
        // Don't clear aggregates on error - keep existing ones if available
        // But if we have calculation indicators, log the error for debugging
        if (hasProtectionLevels || hasQuotationResults) {
          console.error('Failed to load aggregates for quote with calculation results:', data.quote.id, error);
        }
      } finally {
        setLoadingAggregates(false);
      }
    };

    loadAggregates();
  }, [data.quote?.id, data.quote?.quotationResults, data.quote?.protectionLevels]);

  // Missing state variables from original implementation
  // const [isCalculating, setIsCalculating] = useState(false);
  // const [protectionOptions, setProtectionOptions] = useState<Record<string, unknown>>({});
  // const [selectedLevels, setSelectedLevels] = useState<Record<string, number>>({});
  
  const quotation = useQuotation();

  // Note: Removed unused errorArray - validation now handled by SimpleWizardShell
  // const protectionLevelError = errorArray.find(e => e.includes('protection') || e.includes('level'));
  // const pricingError = errorArray.find(e => e.includes('pricing') || e.includes('premium'));

  // Check if quotation is supported for this product
  const selectedPerils = data.quote?.perils_config?.perils?.map((p: { peril_id: string }) => p.peril_id) || [];
  const quotationSupport = quotation ? quotation.validateSupport(selectedPerils) : { isSupported: false, supported: [], unsupported: selectedPerils };

  // Generate fingerprint of calculation parameters to detect changes
  const getCalculationFingerprint = useCallback(() => {
    const currentPerils = data.quote?.perils_config?.perils?.map((p: { peril_id: string }) => p.peril_id) || [];
    return JSON.stringify({
      productId: data.quote?.productId,
      perils: currentPerils.sort(),
      perilWeights: data.quote?.perils_config?.perils?.reduce((weights: Record<string, number>, p: { peril_id: string; weight?: number }) => ({ ...weights, [p.peril_id]: p.weight || 0 }), {} as Record<string, number>) || {},
      coverageStartDate: data.quote?.coverageStartDate,
      coverageEndDate: data.quote?.coverageEndDate,
      sumInsured: data.quote?.participants?.reduce((total, p) => total + (Number(p.sumInsured) || 0), 0) || 0,
      participants: data.quote?.participants?.map(p => ({ 
        farmerId: p.farmerId, 
        role: p.role 
      })) || [],
      policyType: data.quote?.isMasterPolicy ? 'master' : 'individual',
      quotationScript: (data.quote?.product as unknown as Record<string, unknown>)?.quotationScript as string
    });
  }, [data]);

  // Check if calculation parameters have changed since last calculation
  // const hasCalculationParametersChanged = useCallback((): boolean => {
  //   if (!data.quotationResults) {
  //     return true; // No previous calculation, so we need to calculate
  //   }
  //
  //   // Get current calculation fingerprint
  //   const currentFingerprint = getCalculationFingerprint();
  //   
  //   // Get stored calculation fingerprint (if available)
  //   const storedResults = data.quotationResults as Record<string, unknown>;
  //   const storedFingerprint = storedResults.calculationFingerprint;
  //   
  //   if (!storedFingerprint) {
  //     // No stored fingerprint means this is an old quote without fingerprint
  //     // Don't show "parameters changed" for old quotes - assume they're still valid
  //     return false;
  //   }
  //   
  //   // Compare fingerprints
  //   const hasChanged = currentFingerprint !== storedFingerprint;
  //   
  //   
  //   return hasChanged;
  // }, [data, getCalculationFingerprint]);

  // Build quotation request for R-Server

  // Start quotation calculation
  const startQuotationCalculation = useCallback(async () => {
    if (!data.quote?.id) {
      setCalculationError('Quote ID is required for quotation calculation');
      return;
    }

    if (!quotation) {
      setCalculationError('Quotation service not available');
      return;
    }

    if (!quotationSupport.isSupported) {
      setCalculationError(`Quotation not supported for perils: ${quotationSupport.unsupported.join(', ')}`);
      return;
    }

    try {
      setCalculationError(null);
      setIsDirty(false);
      // Note: setParametersChanged removed - parameters no longer tracked
      
      // Clear recalc needed flag since we're starting a new calculation
      updateData({ 
        recalcNeeded: false
      });
      setShowRecalcPrompt(false);
      
      // Clear any previous quotation state for clean initialization
      quotation.clearResults();
      
      // Reset local component state
      setProtectionSliders([]);
      setIsCalculationComplete(false);
      setQuotationMetadata(null);
      freshResultsProcessedRef.current = false; // Reset flag for new calculation
      
      
      // Start calculation with quotation service (backend builds request from quote data)
      await quotation.startCalculation({ quoteId: data.quote.id }, (result) => {
        // Store results directly in Zustand when they arrive
        
        // Transform and store results immediately in wizard data
        processAndStoreResults(result);
      });
      
      // Job ID will be stored via the callback
      
    } catch (error) {
      console.error('🔴 QUOTATION CALCULATION ERROR:', error);
      console.error('🔴 ERROR STACK:', error instanceof Error ? error.stack : 'No stack trace');
      const errorMessage = error instanceof Error ? error.message : String(error);
      setCalculationError(errorMessage);
      
      // Error will be stored in quote.quotationResults by backend when it detects failed job status
      // No need to store in wizard data - just clear recalc flag
      updateData({ 
        recalcNeeded: false // Clear recalc flag since we tried and failed
      });
    }
  }, [data.quote?.id, quotation, quotationSupport, updateData]);

  // Detect available perils from raw scenario data
  const detectAvailablePerils = useCallback((scenarios: RawScenarioData[]): string[] => {
    const perils = new Set<string>();
    
    scenarios.forEach(scenario => {
      Object.keys(scenario).forEach(key => {
        if (key.includes('_premium') && scenario[key]) {
          const perilName = key.replace('_premium', '');
          perils.add(perilName);
        }
      });
    });
    
    return Array.from(perils);
  }, []);

  // Extract peril-specific data from raw scenarios
  const extractPerilData = useCallback((scenarios: RawScenarioData[], perilId: string) => {
    return scenarios.map(scenario => ({
      strikeReturnPeriod: scenario[`${perilId}_strike_return_period`] as number || 1,
      exhaustReturnPeriod: scenario[`${perilId}_exhaust_return_period`] as number || 1,
      strikeThreshold: scenario[`${perilId}_strike_threshold`] as number || 0,
      exhaustThreshold: scenario[`${perilId}_exhaust_threshold`] as number || 0,
      premium: scenario[`${perilId}_premium`] as number || 0,
      paymentPerMm: scenario[`${perilId}_payment_per_mm`] as number || 0,
      totalPayable: scenario.total_payable as number || 0,
      taxAmount: scenario.tax_amount as number || 0,
      subsidyAmount: scenario.subsidy_amount as number || 0,
      anrp: scenario.ANRP as number || 0,
      grp: scenario.GRP as number || 0,
      nrp: scenario.NRP as number || 0,
    }));
  }, []);

  // Process and store results directly in Zustand (single source of truth)
  const processAndStoreResults = useCallback((result: QuotationResult) => {

    // Collect all scenarios from all farmers
    const allScenarios: RawScenarioData[] = result.rawData.farmers.flatMap(farmer => farmer.scenarios);
    
    // Detect available perils dynamically
    const availablePerils = detectAvailablePerils(allScenarios);

    // Create sliders for each peril
    const sliders: ProtectionLevelSlider[] = [];
    
    availablePerils.forEach(perilId => {
      const perilData = extractPerilData(allScenarios, perilId);
      
      // Get unique return periods for this peril
      const availableStrikePeriods = [...new Set(perilData.map(s => s.strikeReturnPeriod))].sort((a, b) => a - b);
      const availableExhaustPeriods = [...new Set(perilData.map(s => s.exhaustReturnPeriod))].sort((a, b) => a - b);
      
      const slider: ProtectionLevelSlider = {
        peril: perilId,
        perilName: perilId === 'Deficit' ? 'Drought / Deficit' : 
                   perilId === 'Excess' ? 'Excess Rain / Flooding' :
                   perilId === 'Germination' ? 'Dry Spell / Germination' :
                   perilId === 'PoorRangelands' ? 'Poor Rangelands' : perilId,
        currentStrike: availableStrikePeriods[0] || 4,
        currentExhaust: availableExhaustPeriods[0] || 55,
        availableStrikePeriods,
        availableExhaustPeriods,
        availableOptions: perilData.map(scenario => ({
          strike: scenario.strikeReturnPeriod,
          exhaust: scenario.exhaustReturnPeriod,
          premium: scenario.premium,
          protectionLevel: 1, // Default
          totalPayable: scenario.totalPayable,
          strikeThreshold: scenario.strikeThreshold,
          exhaustThreshold: scenario.exhaustThreshold,
          paymentPerMm: scenario.paymentPerMm,
          anrp: scenario.anrp,
          grp: scenario.grp,
          taxAmount: scenario.taxAmount,
          subsidyAmount: scenario.subsidyAmount,
        }))
      };
      
      sliders.push(slider);
    });


    // Store everything in Zustand (single source of truth)
    const currentFingerprint = getCalculationFingerprint();
    
    // Build protection levels for ALL perils (not just the first one)
    const protectionLevels: ProtectionLevels = {};
    const selectedScenarios: Record<string, { strikeReturnPeriod: number; exhaustReturnPeriod: number }> = {};
    
    sliders.forEach(slider => {
      const firstOption = slider.availableOptions[0];
      if (firstOption) {
        protectionLevels[slider.peril] = {
          strike_level: firstOption.strike,
          exhaust_level: firstOption.exhaust,
          strike_value: firstOption.strike,
          exhaust_value: firstOption.exhaust,
          units: 'mm'
        };
        selectedScenarios[slider.peril] = {
          strikeReturnPeriod: firstOption.strike,
          exhaustReturnPeriod: firstOption.exhaust
        };
      }
    });
    
    // Update quote object with calculation results (single source of truth)
    const updatedQuote = createCompleteQuote(data, {
      quotationJobId: result.jobId,
      quotationResults: {
        rawData: result.rawData, // Store complete raw R-Server data
        calculationFingerprint: currentFingerprint,
        processedSliders: sliders, // Store processed multi-peril sliders
        metadata: result.rawData.metadata
      },
      protectionLevels,
      selectedScenarios
    });
    
    // Store quote first (without pricingCalculation - will be updated after aggregates load)
    updateData({ 
      quote: updatedQuote,
      recalcNeeded: false
    });
    
    // Load aggregates immediately after storing results to get accurate totals
    // This ensures pricingCalculation uses aggregated values for all farmers
    const loadAggregatesForPricing = async () => {
      try {
        const response = await quoteService.getQuotationSummary(updatedQuote.id);
        const aggregatesData = response?.quotationResults?.aggregates;
        
        if (aggregatesData && aggregatesData.total && aggregatesData.total.total_payable > 0) {
          setAggregates(aggregatesData);
          
          // Update pricing calculation with aggregated values (all farmers combined)
          updateData({ 
            pricingCalculation: {
              totalPremium: aggregatesData.total.total_payable,
              breakdown: {
                anrp: aggregatesData.total.ANRP || 0,
                loadings: (aggregatesData.total.GRP || 0) - (aggregatesData.total.ANRP || 0),
                fees: 0,
                taxes: aggregatesData.total.tax_amount || 0,
                subsidies: -(aggregatesData.total.subsidy_amount || 0)
              }
            }
          });
        } else {
          // Fallback: use first scenario if aggregates not available (shouldn't happen)
          const firstScenario = sliders[0]?.availableOptions[0];
          if (firstScenario) {
            updateData({ 
              pricingCalculation: {
        totalPremium: firstScenario.premium,
        breakdown: {
          anrp: firstScenario.anrp,
          loadings: firstScenario.grp - firstScenario.anrp,
          fees: 0,
          taxes: firstScenario.taxAmount,
          subsidies: -firstScenario.subsidyAmount
        }
              }
            });
          }
        }
      } catch (error) {
        console.error('Error loading aggregates after calculation:', error);
        // Fallback: use first scenario
        const firstScenario = sliders[0]?.availableOptions[0];
        if (firstScenario) {
          updateData({ 
            pricingCalculation: {
              totalPremium: firstScenario.premium,
              breakdown: {
                anrp: firstScenario.anrp,
                loadings: firstScenario.grp - firstScenario.anrp,
                fees: 0,
                taxes: firstScenario.taxAmount,
                subsidies: -firstScenario.subsidyAmount
              }
            }
          });
        }
      }
    };
    
    // Load aggregates asynchronously
    loadAggregatesForPricing();
    
    // Also clear the recalc prompt UI
    setShowRecalcPrompt(false);

    // Update component state from Zustand data
    setProtectionSliders(sliders);
    setIsCalculationComplete(true);
    // setParametersChanged - removed(false);
    
    // Set quotation metadata
    if (result.rawData.metadata) {
      setQuotationMetadata(result.rawData.metadata as QuotationMetadata);
    }
  }, [detectAvailablePerils, extractPerilData, getCalculationFingerprint, updateData]);

  // Load existing quotation results if available (when returning to Step 5 or editing)
  // This useEffect handles the fallback case for old format data transformation
  useEffect(() => {
    // Skip if we already have sliders loaded from initial state (new format)
    if (protectionSliders.length > 0 && isCalculationComplete) {
      return;
    }
    
    // Check if we have calculation results (quotationResults or protectionLevels indicate calculation was done)
    const hasQuotationResults = !!data.quote?.quotationResults;
    const hasProtectionLevels = !!data.quote?.protectionLevels && Object.keys(data.quote.protectionLevels).length > 0;
    
    if ((hasQuotationResults || hasProtectionLevels) && !isCalculationComplete && data.quote) {
      const storedResults = data.quote.quotationResults as Record<string, unknown>;
      
      // Use processed sliders if available (new format)
      if (storedResults?.processedSliders) {
        setProtectionSliders(storedResults.processedSliders as ProtectionLevelSlider[]);
        setIsCalculationComplete(true);
        
        // Set quotation metadata if available
        if (storedResults.metadata) {
          setQuotationMetadata(storedResults.metadata as QuotationMetadata);
        }
        return;
      }
      
      // Fallback: Extract results from stored quotation data (old format)
      if (!data.quote) {
        return;
      }
      
      const storedQuotationData = data.quote.quotationResults as StoredQuotationResults;
      const quotationResults = storedQuotationData.output?.quotation_result || [];
      
      if (quotationResults.length > 0) {
        
        // Transform stored results into sliders (same logic as fresh results)
        const transformedResults = quotationResults.flatMap(farmer => 
          (farmer.data || []).flatMap(scenario => {
            const results: QuotationScenario[] = [];
            
            // Extract deficit peril data if present
            if (scenario.deficit_strike_return_period !== undefined) {
              results.push({
                peril: 'Deficit',
                strikeReturnPeriod: scenario.deficit_strike_return_period as number || 1,
                exhaustReturnPeriod: scenario.deficit_exhaust_return_period as number || 20,
                strikeThreshold: scenario.deficit_strike_threshold as number || 0,
                exhaustThreshold: scenario.deficit_exhaust_threshold as number || 0,
                premium: scenario.deficit_premium as number || 0,
                totalPayable: scenario.total_payable as number || 0,
                paymentPerMm: scenario.deficit_payment_per_mm as number || 0,
                anrp: scenario.ANRP as number || 0,
                grp: scenario.GRP as number || 0,
                taxAmount: scenario.tax_amount as number || 0,
                subsidyAmount: scenario.subsidy_amount as number || 0
              });
            }
            
            // Extract excess peril data if present
            if (scenario.excess_strike_return_period !== undefined) {
              results.push({
                peril: 'Excess',
                strikeReturnPeriod: scenario.excess_strike_return_period as number || 1,
                exhaustReturnPeriod: scenario.excess_exhaust_return_period as number || 20,
                strikeThreshold: scenario.excess_strike_threshold as number || 0,
                exhaustThreshold: scenario.excess_exhaust_threshold as number || 0,
                premium: scenario.excess_premium as number || 0,
                totalPayable: scenario.total_payable as number || 0,
                paymentPerMm: scenario.excess_payment_per_mm as number || 0,
                anrp: scenario.ANRP as number || 0,
                grp: scenario.GRP as number || 0,
                taxAmount: scenario.tax_amount as number || 0,
                subsidyAmount: scenario.subsidy_amount as number || 0
              });
            }
            
            return results;
          }) || []
        );
        
        
        // Process the stored results the same way as fresh results
        const sliders: ProtectionLevelSlider[] = [];
        const perilGroups: Record<string, QuotationScenario[]> = {};
        
        transformedResults.forEach((result: QuotationScenario) => {
          const peril = result.peril || 'Deficit';
          if (!perilGroups[peril]) {
            perilGroups[peril] = [];
          }
          perilGroups[peril].push(result as QuotationScenario);
        });
        
        Object.entries(perilGroups).forEach(([peril, scenarios]) => {
          const availableStrikePeriods = [...new Set(scenarios.map(s => s.strikeReturnPeriod).filter((x): x is number => typeof x === 'number'))].sort((a, b) => a - b);
          const availableExhaustPeriods = [...new Set(scenarios.map(s => s.exhaustReturnPeriod).filter((x): x is number => typeof x === 'number'))].sort((a, b) => a - b);
          
          // Use saved protection levels or selected scenarios if available, otherwise default to first option
          const savedScenario = data.quote?.selectedScenarios?.[peril] ?? data.quote?.selectedScenarios?.[peril.toLowerCase()];
          const savedProtectionLevel = data.quote?.protectionLevels?.[peril] ?? data.quote?.protectionLevels?.[peril.toLowerCase()];
          
          const currentStrike = (savedProtectionLevel as unknown as Record<string, unknown>)?.strike_level as number || 
                               (savedProtectionLevel as unknown as Record<string, unknown>)?.strikeLevel as number ||
                               (savedScenario as Record<string, unknown>)?.strikeReturnPeriod as number || 
                               availableStrikePeriods[0] || 4;
          const currentExhaust = (savedProtectionLevel as unknown as Record<string, unknown>)?.exhaust_level as number ||
                                (savedProtectionLevel as unknown as Record<string, unknown>)?.exhaustLevel as number || 
                                (savedScenario as Record<string, unknown>)?.exhaustReturnPeriod as number || 
                                availableExhaustPeriods[0] || 55;
          
          
          const slider: ProtectionLevelSlider = {
            peril: peril,
            perilName: peril === 'Deficit' ? 'Drought / Deficit' : 
                       peril === 'Excess' ? 'Excess Rain / Flooding' :
                       peril === 'Germination' ? 'Dry Spell / Germination' :
                       peril === 'PoorRangelands' ? 'Poor Rangelands' : peril,
            currentStrike,
            currentExhaust,
            availableStrikePeriods,
            availableExhaustPeriods,
            availableOptions: scenarios.map(scenario => ({
              strike: scenario.strikeReturnPeriod || 0,
              exhaust: scenario.exhaustReturnPeriod || 0,
              premium: scenario.premium || 0,
              protectionLevel: scenario.protectionLevel || 1,
              totalPayable: scenario.totalPayable || 0,
              strikeThreshold: scenario.strikeThreshold || 0,
              exhaustThreshold: scenario.exhaustThreshold || 0,
              paymentPerMm: scenario.paymentPerMm || 0,
              anrp: scenario.anrp || 0,
              grp: scenario.grp || 0,
              taxAmount: scenario.taxAmount || 0,
              subsidyAmount: scenario.subsidyAmount || 0,
            }))
          };
          
          sliders.push(slider);
        });
        
        setProtectionSliders(sliders);
        setIsCalculationComplete(true);
        
        // Set pricing calculation from first scenario to show pricing card
        if (sliders.length > 0 && sliders[0].availableOptions.length > 0) {
          const firstScenario = sliders[0].availableOptions[0];
          updateData({
            pricingCalculation: {
              totalPremium: firstScenario.premium,
              breakdown: {
                anrp: firstScenario.anrp,
                loadings: firstScenario.grp - firstScenario.anrp,
                fees: 0,
                taxes: firstScenario.taxAmount,
                subsidies: -firstScenario.subsidyAmount
              }
            }
          });
        }
        
        // Set quotation metadata from stored results
        if (storedQuotationData.output?.metadata) {
          setQuotationMetadata(storedQuotationData.output.metadata);
        }
        
        // Parameters changed detection now handled by recalcNeeded flag from Zustand
      }
    }
  }, [data.quote?.quotationResults, data.quote?.selectedScenarios, isCalculationComplete, quotation?.isCalculating, protectionSliders.length]);

  const handleProtectionLevelChange = useCallback(async (peril: string, strike: number, exhaust: number) => {
    // Update slider visual state immediately for responsiveness
    setProtectionSliders(prev => prev.map(slider => 
      slider.peril === peril 
        ? { ...slider, currentStrike: strike, currentExhaust: exhaust }
        : slider
    ));

    const updatedLevels = {
      ...data.quote?.protectionLevels,
      [peril]: {
        strike_level: strike,
        exhaust_level: exhaust,
        strike_value: strike,
        exhaust_value: exhaust,
        units: 'mm'
      }
    };

    // Update quote object with new protection levels
    const updatedQuote = createCompleteQuote(data, {
      protectionLevels: updatedLevels as unknown as ProtectionLevels,
      selectedScenarios: {
        ...data.quote?.selectedScenarios,
        [peril]: { strikeReturnPeriod: strike, exhaustReturnPeriod: exhaust }
      }
    });
    
    // Update local state immediately
    updateData({ quote: updatedQuote });

    // Call backend to get updated aggregates with new protection levels
    if (data.quote?.id) {
      try {
        const response = await quoteService.getQuotationSummary(data.quote.id, updatedLevels);
        
        // Update aggregates state with backend calculation
        const aggregatesData = response?.quotationResults?.aggregates;
        if (aggregatesData) {
          setAggregates(aggregatesData);
          
          // Update pricingCalculation for backward compatibility (using aggregated values)
          updateData({
            pricingCalculation: {
              totalPremium: aggregatesData.total.total_payable,
              breakdown: {
                anrp: aggregatesData.total.ANRP || 0,
                loadings: (aggregatesData.total.GRP || 0) - (aggregatesData.total.ANRP || 0),
                fees: 0,
                taxes: aggregatesData.total.tax_amount || 0,
                subsidies: -(aggregatesData.total.subsidy_amount || 0) // Negate subsidy (it's a reduction)
              }
            }
          });
        }
      } catch (error) {
        console.error('Error updating protection levels and aggregates:', error);
      }
    }
  }, [data.quote?.protectionLevels, data.quote?.id, updateData]);

  // Remove mock pricing calculation - use real quotation service only

  // const selectedPerils = data.perils || [];
  // const allLevelsSelected = selectedPerils.every(peril => selectedLevels[peril]);
  // const totalPremium = data.pricingCalculation?.totalPremium || 0;

  // Check if quote is approved (read-only mode)
  const isReadOnly = data.quote?.state === 'approved';

  return (
    <div className={`space-y-6 ${isReadOnly ? 'pointer-events-none opacity-60' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldIcon className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Protection Level Selection</h4>
        </div>
        {isReadOnly && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Approved</span>
          </div>
        )}
      </div>

      {/* Quotation Support Check */}
      {!quotationSupport.isSupported && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertCircleIcon className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Manual Pricing Required</p>
                <p className="text-sm text-yellow-700">
                  This product contains perils not supported by automated quotation: {quotationSupport.unsupported.join(', ')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Protection Levels & Pricing */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isDirty && isCalculationComplete && (
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                Recalculation needed
              </span>
            )}
          </div>
          {/* Show Recalculate button if: can recalculate, calculation is dirty, has errors, or has zero values */}
          {(quotation?.canRecalculate || 
            (isCalculationComplete && isDirty) || 
            quotation?.error || 
            calculationError || 
            quotation?.progress?.stage === 'error' ||
            (aggregates && aggregates.total && aggregates.total.total_payable === 0) // Zero values detected
          ) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (isDirty && isCalculationComplete) {
                  // Show confirmation dialog for recalculation
                  if (window.confirm('Recalculation will save the draft and clear previous calculation results. Proceed?')) {
                    setIsDirty(false);
                    setIsCalculationComplete(false);
                    setProtectionSliders([]);
                    setQuotationMetadata(null);
                    freshResultsProcessedRef.current = false; // Reset flag for recalculation
                    // setLastCalculationParams(null); // Commented out - not currently used
                    // Note: selectedScenarios reset handled in quote object during recalculation
                    startQuotationCalculation();
                  }
                } else {
                  // If there's an error or can recalculate, just start a new calculation
                  startQuotationCalculation();
                }
              }}
              className="flex items-center space-x-2"
            >
              <RefreshCwIcon className="w-4 h-4" />
              <span>Recalculate</span>
            </Button>
          )}
        </div>

        {/* Calculation in Progress */}
        {quotation?.isCalculating && quotation?.progress?.stage !== 'error' && (
          <div className="text-center py-8">
            <ClockIcon className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Calculating Quote</h3>
            <p className="text-gray-600 mt-2">
              {quotation?.progress?.message || 'Submitting request to quotation service...'}
            </p>
            {quotation?.progress?.estimatedTimeRemaining && (
              <p className="text-sm text-gray-500 mt-1">
                Estimated time remaining: ~{Math.ceil(quotation.progress.estimatedTimeRemaining)} seconds
              </p>
            )}
          </div>
        )}

        {/* Calculation Error - Show with highest priority when any error exists */}
        {(quotation?.error || calculationError || quotation?.progress?.stage === 'error') && (
          <div className="mb-6 p-6 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircleIcon className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-red-900 mb-2">Calculation Failed</h3>
                <pre className="text-red-700 whitespace-pre-wrap break-words font-sans text-sm overflow-wrap-anywhere">
                  {quotation?.error || calculationError || quotation?.progress?.message || 'An error occurred during calculation'}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Recalculation Needed - Show when data.recalcNeeded is true */}
        {showRecalcPrompt && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Quote Data Changed</h4>
                <p className="mt-1 text-sm text-yellow-700">
                  The quote data (sum insured, perils, coverage dates, or participants) has been modified since the last calculation. 
                  The current protection levels may no longer be accurate.
                </p>
                <div className="mt-3 flex space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowRecalcPrompt(false);
                      startQuotationCalculation();
                    }}
                    className="bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200"
                  >
                    Recalculate Now
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRecalcPrompt(false);
                      updateData({ recalcNeeded: false });
                    }}
                    className="text-sm text-yellow-600 hover:text-yellow-800 underline"
                  >
                    Continue with current results
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manual Start Button - No calculation has been started */}
        {quotation && !quotation.isCalculating && !isCalculationComplete && !quotation.error && !calculationError && quotation?.progress?.stage !== 'error' && quotationSupport.isSupported && (
          <div className="text-center py-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ready to Calculate Protection Levels</h3>
            <p className="text-gray-600 mb-6">Click below to start the quotation calculation process.</p>
            <Button 
              onClick={() => {
                console.log('🔘 Manual quotation start');
                startQuotationCalculation();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
            >
              Start Quotation Calculation
            </Button>
          </div>
        )}

        {/* Show Error - Quotation Service Not Available (only if no existing results) */}
        {!quotation && !isCalculationComplete && (
          <div className="text-center py-8">
            <AlertCircleIcon className="w-8 h-8 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-900">Quotation Service Not Available</h3>
            <p className="text-red-600 mt-2 mb-4">
              The quotation calculation service (useQuotation hook) is not connected.
              Real protection levels and pricing require the quotation service integration.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left max-w-md mx-auto">
              <p className="text-sm text-red-800 font-medium mb-2">Missing Dependencies:</p>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• useQuotation hook from ../../../hooks/useQuotation</li>
                <li>• QuotationProgress component</li>
                <li>• quotationService integration</li>
                <li>• R-Server API connection</li>
              </ul>
            </div>
          </div>
        )}

        {/* Results Available - Show Protection Level Sliders */}
        {isCalculationComplete && protectionSliders.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircleIcon className="w-5 h-5" />
              <span className="font-medium">Quotation calculation completed successfully</span>
            </div>

            {protectionSliders.map((slider) => {
              const strikeIsSingle = slider.availableStrikePeriods.length <= 1;
              const exhaustIsSingle = slider.availableExhaustPeriods.length <= 1;

              const formatTickLabels = (periods: number[]): string => {
                if (periods.length <= 10) return periods.join(', ');
                const interval = Math.ceil(periods.length / 10);
                return periods
                  .filter((_, i) => i === 0 || i === periods.length - 1 || i % interval === 0)
                  .join(', ');
              };

              return (
                <div key={slider.peril} className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">{slider.perilName}</h4>
                  
                  <div className="grid grid-cols-2 gap-6">
                    {/* Strike Return Period */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Strike Return Period: {slider.currentStrike} years
                      </label>
                      {strikeIsSingle ? (
                        <div className="bg-gray-100 rounded-lg px-4 py-2 text-sm font-medium text-gray-700">
                          {slider.availableStrikePeriods[0]} years (fixed)
                        </div>
                      ) : (
                        <>
                          <input
                            type="range"
                            value={slider.availableStrikePeriods.findIndex(period => period === slider.currentStrike)}
                            onChange={(e) => {
                              const periodIndex = parseInt(e.target.value);
                              const selectedPeriod = slider.availableStrikePeriods[periodIndex];
                              if (selectedPeriod !== undefined) {
                                handleProtectionLevelChange(slider.peril, selectedPeriod, slider.currentExhaust);
                              }
                            }}
                            min={0}
                            max={slider.availableStrikePeriods.length - 1}
                            step={1}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <p className="text-xs text-gray-500">
                            Available: {formatTickLabels(slider.availableStrikePeriods)} years
                          </p>
                        </>
                      )}
                    </div>

                    {/* Exhaust Return Period */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Exhaust Return Period: {slider.currentExhaust} years
                      </label>
                      {exhaustIsSingle ? (
                        <div className="bg-gray-100 rounded-lg px-4 py-2 text-sm font-medium text-gray-700">
                          {slider.availableExhaustPeriods[0]} years (fixed)
                        </div>
                      ) : (
                        <>
                          <input
                            type="range"
                            value={slider.availableExhaustPeriods.findIndex(period => period === slider.currentExhaust)}
                            onChange={(e) => {
                              const periodIndex = parseInt(e.target.value);
                              const selectedPeriod = slider.availableExhaustPeriods[periodIndex];
                              if (selectedPeriod !== undefined) {
                                handleProtectionLevelChange(slider.peril, slider.currentStrike, selectedPeriod);
                              }
                            }}
                            min={0}
                            max={slider.availableExhaustPeriods.length - 1}
                            step={1}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <p className="text-xs text-gray-500">
                            Available: {formatTickLabels(slider.availableExhaustPeriods)} years
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Pricing Summary */}
            {/* Show card if we have calculation results (pricingCalculation, aggregates, or calculation indicators) */}
            {(() => {
              const hasPricingCalculation = !!data.pricingCalculation;
              const hasAggregates = !!aggregates;
              const hasProtectionLevels = !!(data.quote?.protectionLevels && Object.keys(data.quote.protectionLevels).length > 0);
              const hasQuotationResults = !!data.quote?.quotationResults;
              return hasPricingCalculation || hasAggregates || hasProtectionLevels || hasQuotationResults;
            })() && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Tab Headers */}
                <div className="flex border-b border-gray-200 bg-gray-50">
                  <button
                    onClick={() => setActiveTab('pricing')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'pricing'
                        ? 'bg-white border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    💰 Pricing Summary
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'details'
                        ? 'bg-white border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    📋 Calculation Details
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'pricing' && (
                    <div>
                      {/* Loading state */}
                      {loadingAggregates ? (
                        <div className="text-center py-4">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            <span className="text-sm text-gray-600">Loading calculation results...</span>
                          </div>
                        </div>
                      ) : (() => {
                        // Always use backend aggregates for accurate totals (aggregated across all farmers)
                        // Aggregates are the single source of truth for financial values
                        let totalPayable = 0;
                        let totalAnrp = 0;
                        let totalGrp = 0;
                        let totalTax = 0;
                        let totalSubsidy = 0;
                        let totalPaymentPerMm = 0;
                        
                        // Get currently selected scenarios for all perils (for display of strike/exhaust values only)
                        const allSelectedScenarios = protectionSliders.map(slider => {
                          const savedLevel = data.quote?.protectionLevels?.[slider.peril] as unknown as Record<string, unknown>;
                          const currentSelection = slider.availableOptions.find(opt => 
                            opt.strike === (savedLevel?.strike_level || savedLevel?.strikeLevel) &&
                            opt.exhaust === (savedLevel?.exhaust_level || savedLevel?.exhaustLevel)
                          ) || slider.availableOptions[0];
                          return { peril: slider.perilName, scenario: currentSelection };
                        });

                        // Priority 1: Use backend aggregates (most accurate - aggregated across all farmers)
                        if (aggregates && aggregates.total) {
                          totalPayable = aggregates.total.total_payable || 0;
                          totalAnrp = aggregates.total.ANRP || 0;
                          totalGrp = aggregates.total.GRP || 0;
                          totalTax = aggregates.total.tax_amount || 0;
                          totalSubsidy = aggregates.total.subsidy_amount || 0;
                          totalPaymentPerMm = aggregates.total.payment_per_mm || 0;
                        } 
                        // Priority 2: Use pricingCalculation if aggregates not loaded yet
                        else if (data.pricingCalculation?.totalPremium) {
                          totalPayable = data.pricingCalculation.totalPremium;
                          totalAnrp = data.pricingCalculation.breakdown?.anrp || 0;
                          totalGrp = totalAnrp + (data.pricingCalculation.breakdown?.loadings || 0);
                          totalTax = data.pricingCalculation.breakdown?.taxes || 0;
                          totalSubsidy = -(data.pricingCalculation.breakdown?.subsidies || 0);
                          // paymentPerMm not available in pricingCalculation, will show 0
                        } 
                        // Priority 3: Last resort - aggregate from selected scenarios (less accurate)
                        else if (allSelectedScenarios.length > 0) {
                          // Note: This sums per peril, not per farmer, so may be incorrect for multi-farmer quotes
                          totalPayable = allSelectedScenarios.reduce((sum, { scenario }) => sum + (scenario?.totalPayable || 0), 0);
                          totalAnrp = allSelectedScenarios.reduce((sum, { scenario }) => sum + (scenario?.anrp || 0), 0);
                          totalGrp = allSelectedScenarios.reduce((sum, { scenario }) => sum + (scenario?.grp || 0), 0);
                          totalTax = allSelectedScenarios.reduce((sum, { scenario }) => sum + (scenario?.taxAmount || 0), 0);
                          totalSubsidy = allSelectedScenarios.reduce((sum, { scenario }) => sum + (scenario?.subsidyAmount || 0), 0);
                          totalPaymentPerMm = allSelectedScenarios.reduce((sum, { scenario }) => sum + (scenario?.paymentPerMm || 0), 0);
                        }
                        // No data available - don't show pricing summary
                        else {
                          return null;
                        }
                        
                        // Check if calculation was done (indicated by protectionLevels or quotationResults)
                        const hasCalculationIndicators = !!(data.quote?.protectionLevels && Object.keys(data.quote.protectionLevels).length > 0) || !!data.quote?.quotationResults;
                        
                        // Always show pricing summary if we have calculation results (aggregates, pricingCalculation, or scenarios)
                        // This ensures results are displayed when editing a quote
                        const hasCalculationResults = !!aggregates || !!data.pricingCalculation?.totalPremium || allSelectedScenarios.length > 0;
                        
                        // If we have calculation indicators but no results, show an error message
                        if (hasCalculationIndicators && !hasCalculationResults) {
                          return (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-start">
                                <AlertCircleIcon className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                                <div>
                                  <h4 className="text-sm font-medium text-red-800">Error: Calculation Results Not Available</h4>
                                  <p className="mt-1 text-sm text-red-700">
                                    This quote has calculation results, but the pricing data could not be loaded. Please try recalculating the quote or contact support if the issue persists.
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        if (!hasCalculationResults) {
                          return null;
                        }

                        // Check if we have zero values (error situation)
                        const hasZeroValues = aggregates && aggregates.total && aggregates.total.total_payable === 0;

                        return (
                          <div className="space-y-4">
                            {/* Warning when values are zero (error situation) */}
                            {hasZeroValues && (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start">
                                  <AlertCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                                  <div>
                                    <h4 className="text-sm font-medium text-yellow-800">Warning: Zero Values Detected</h4>
                                    <p className="mt-1 text-sm text-yellow-700">
                                      The calculation completed but returned zero values. This may indicate an error in the calculation or missing data. Please verify the quote parameters and recalculate if necessary.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Perils table: strike/exhaust per peril */}
                            {allSelectedScenarios.length > 0 && (
                              <div className="mb-4">
                                <h6 className="text-sm font-medium text-gray-700 mb-2">Coverage Levels by Peril</h6>
                                <table className="min-w-full text-sm border border-gray-200 rounded">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Peril</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Strike Return Period</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Exhaust Return Period</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {allSelectedScenarios.map(({ peril, scenario }) => (
                                      <tr key={peril}>
                                        <td className="px-3 py-2 text-gray-900">{peril}</td>
                                        <td className="px-3 py-2 text-right font-medium">{scenario?.strike} years</td>
                                        <td className="px-3 py-2 text-right font-medium">{scenario?.exhaust} years</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Financial values */}
                            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">ANRP (Base Premium):</span>
                                <span className="font-medium">{formatPricingCurrency(totalAnrp)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Loadings:</span>
                                <span className="font-medium">{formatPricingCurrency(totalGrp - totalAnrp)}</span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-gray-600">GRP (Gross Premium):</span>
                                <span className="font-medium">{formatPricingCurrency(totalGrp)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Tax Amount:</span>
                                <span className="font-medium">{formatPricingCurrency(totalTax)}</span>
                              </div>

                              <div className="flex justify-between">
                                <span className="text-gray-600">Payment per unit:</span>
                                <span className="font-medium">{formatPricingCurrency(totalPaymentPerMm)}/unit</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Subsidy Amount:</span>
                                <span className="font-medium text-green-600">-{formatPricingCurrency(totalSubsidy)}</span>
                              </div>
                            </div>
                            
                            {/* Total Payable in bottom right corner */}
                            <div className="border-t border-green-300 pt-4 flex justify-end">
                              <div className="text-right">
                                <div className="text-lg font-bold text-green-800">
                                  Total Payable: {formatPricingCurrency(totalPayable)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {activeTab === 'details' && (
                    <div>
                      {/* Calculation Details Content */}
                      {quotationMetadata ? (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-blue-600/70 font-medium">Script Version:</div>
                            <div className="font-mono">
                              {quotationMetadata.quotationScript ? String(quotationMetadata.quotationScript) : 'N/A'}
                              {quotationMetadata.gitCommit && typeof quotationMetadata.gitCommit === 'string' ? `+${quotationMetadata.gitCommit.substring(0, 7)}` : ''}
                            </div>
                          </div>
                          <div>
                            <div className="text-blue-600/70 font-medium">Processing Time:</div>
                            <div>{quotationMetadata.processingTimeSeconds ? `${String(quotationMetadata.processingTimeSeconds)}s` : 'N/A'}</div>
                          </div>
                          <div>
                            <div className="text-blue-600/70 font-medium">Coverage Period:</div>
                            <div className="font-mono text-xs">{String(quotationMetadata.coveragePeriod || 'N/A')}</div>
                          </div>
                          <div>
                            <div className="text-blue-600/70 font-medium">Calculation Date:</div>
                            <div className="font-mono text-xs">{quotationMetadata.calculationDate && typeof quotationMetadata.calculationDate === 'string' ? new Date(quotationMetadata.calculationDate).toLocaleString() : 'N/A'}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500">No calculation details available</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Note: Removed duplicate error display - now handled by SimpleWizardShell */}
    </div>
  );
}
